import { useState } from "react";
import { Search, UserPlus, ExternalLink, Loader2, ShieldCheck, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { FinderResults } from "./FinderResults";
import { runValidation, isFlagged } from "./validation";

export function FinderPanel({ slot, onChange }) {
  const [query, setQuery] = useState("");
  const [industry, setIndustry] = useState("");
  const [limit, setLimit] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [company, setCompany] = useState("");
  const [vals, setVals] = useState({});
  const [validating, setValidating] = useState(false);

  const validate = async () => {
    setValidating(true);
    const targets = sel.length ? sel : data.results.map((r) => r.email);
    const res = await runValidation(targets);
    if (res) setVals((v) => ({ ...v, ...res }));
    setValidating(false);
  };

  const flagged = data ? data.results.filter((r) => isFlagged(vals[r.email])) : [];
  const removeFlagged = () => {
    const out = new Set(flagged.map((r) => r.email));
    setData({ ...data, results: data.results.filter((r) => !out.has(r.email)) });
    setSel((s) => s.filter((e) => !out.has(e)));
    toast.success(`Removed ${out.size} flagged lead(s) from the list`);
  };

  const search = async (e) => {
    e.preventDefault();
    setBusy(true);
    setData(null);
    setVals({});
    try {
      const { data: d } = await api.post(`/projects/${slot}/finder`, { query, industry, limit: limit ? Number(limit) : null });
      setData(d);
      setCompany(d.company);
      setSel(d.results.filter((r) => r.on_domain && r.mx_ok && !r.in_project).map((r) => r.email));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const addSelected = async () => {
    const multi = data.domains.length > 1;
    try {
      const { data: r } = await api.post(`/projects/${slot}/leads/bulk`, { leads: sel.map((email) => ({ email, company: multi ? data.results.find((x) => x.email === email)?.company || "" : company })) });
      if (r.duplicates) toast.warning(`Lade till ${r.imported} lead(s) — ${r.duplicates} dubblett(er) skapades inte`, { description: "Dessa e-postadresser finns redan i projektet." });
      else toast.success(`Added ${r.imported} lead(s) to this project`);
      setData({ ...data, results: data.results.map((x) => (sel.includes(x.email) ? { ...x, in_project: true } : x)) });
      setSel([]);
      onChange();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-5" data-testid="finder-panel">
      <form onSubmit={search} className="rounded-xl border border-border bg-card p-6 space-y-3">
        <h3 className="font-display text-lg font-semibold">Find email addresses</h3>
        <p className="text-sm text-muted-foreground">Enter a company domain (e.g. <span className="font-mono">company.se</span>) or company name, and/or an industry to discover several companies. Searches DuckDuckGo and each company's public pages for published addresses.</p>
        <div className="grid md:grid-cols-[1fr_260px_170px_auto] gap-3 items-end">
          <div className="space-y-1.5"><Label>Company name or domain</Label>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="company.se, Company Name AB or a city" className="h-11" data-testid="finder-query-input" /></div>
          <div className="space-y-1.5"><Label>Company industry <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="e.g. IT consulting" className="h-11" data-testid="finder-industry-input" /></div>
          <div className="space-y-1.5"><Label>Number of leads <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input type="number" min={1} max={100} value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="All (max 100)" className="h-11" data-testid="finder-limit-input" /></div>
          <Button type="submit" disabled={busy || (query.trim().length < 2 && industry.trim().length < 2)} className="h-11 px-6" data-testid="finder-search-button">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}{busy ? "Searching…" : "Search"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">With an industry, the company field narrows the search (e.g. a company type or city). Without one, it finds addresses for that single company.</p>
        {busy && <p className="text-xs text-muted-foreground" data-testid="finder-loading">Searching the web and reading contact pages — this can take up to 60 seconds.</p>}
      </form>
      {data && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            {data.domain ? (
              <a href={`https://${data.domain}`} target="_blank" rel="noreferrer" className="font-mono text-sm flex items-center gap-1 hover:underline" data-testid="finder-domain">
                {data.domain}<ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : <span className="text-sm font-medium" data-testid="finder-domain">{data.domains.length} companies in “{data.industry}”</span>}
            <span className="text-xs text-muted-foreground" data-testid="finder-summary">{data.results.length} addresses · {data.pages_checked} pages read</span>
            <div className="ml-auto flex items-center gap-2">
              {data.domain && <Input value={company} onChange={(e) => setCompany(e.target.value)} className="w-48 h-9" placeholder="Company" data-testid="finder-company-input" />}
              <Button variant="secondary" onClick={validate} disabled={validating || !data.results.length} size="sm" className="h-9" data-testid="finder-validate-button">
                {validating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}
                {validating ? "Validating…" : `Validate ${sel.length ? sel.length + " selected" : "all"}`}
              </Button>
              <Button onClick={addSelected} disabled={!sel.length} size="sm" className="h-9" data-testid="finder-add-selected-button">
                <UserPlus className="h-4 w-4 mr-1.5" />Add {sel.length} to leads
              </Button>
            </div>
          </div>
          {flagged.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3" data-testid="finder-flagged-notice">
              <AlertTriangle className="h-4 w-4 text-rose-700 shrink-0" />
              <p className="text-sm text-rose-800 flex-1">
                <strong>{flagged.length} flagged lead(s)</strong> are risky or invalid (catch-all, disposable or rejected mailbox). They are marked in red below.
              </p>
              <Button size="sm" variant="destructive" onClick={removeFlagged} data-testid="finder-remove-flagged-button">
                <Trash2 className="h-4 w-4 mr-1.5" />Remove flagged
              </Button>
            </div>
          )}
          <FinderResults results={data.results} sel={sel} setSel={setSel} showCompany={!data.domain} vals={vals} />
        </div>
      )}
    </div>
  );
}
